import type { AppNotification } from '@/types/domain';

/**
 * 通知的展示逻辑，铃铛和工作台的动态栏共用。
 * 两处各写一份的话，折叠规则迟早改岔（一处合并了另一处没合并）。
 */

const TYPE_LABEL: Readonly<Record<string, string>> = {
  task_overdue: '任务逾期',
  task_due_soon: '即将到期',
  gate_approaching: 'Gate 临近',
  approval_pending: '待审批',
  'review-outcome': '评审结论',
  'plan-node-reminder': '催办',
  'record-reminder': '催办',
  record_owner_assigned: '指派给你',
  project_member_added: '加入项目',
  plan_completion_audit: '完成核查',
};

export function notificationTypeLabel(type: string): string {
  return TYPE_LABEL[type] ?? '提醒';
}

export function notificationWhen(value: string): string {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return '';
  const minutes = Math.floor((Date.now() - time) / 60_000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)} 小时前`;
  return value.slice(0, 10);
}

export interface StackedNotification extends AppNotification {
  repeats: number;
  since: string | null;
}

/**
 * 同一件事的每日重复合并成一条。
 *
 * 提醒是按天生成的：同一个 Gate 没关掉，7-27 到 7-31 就是五条一模一样的
 * 「Gate 临近」，把面板刷满。合并后只显示最新那条，并注明「已连续提醒 N 天」——
 * 重复本身是信息：提醒了五天还没动，比提醒一次更值得注意。
 */
export function stackRepeats(list: AppNotification[]): StackedNotification[] {
  const byKey = new Map<string, StackedNotification>();
  for (const item of list) {
    // 同项目 + 同类型 + 同正文 = 同一件事的重复提醒
    const key = `${item.projectId ?? 'none'}|${item.type}|${item.body ?? ''}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...item, repeats: 1, since: item.createdAt });
      continue;
    }
    existing.repeats += 1;
    // 保留最新一条的身份（点击标已读的是它），起始时间取最早
    if (item.createdAt > existing.createdAt) {
      Object.assign(existing, item, { repeats: existing.repeats, since: existing.since });
    }
    if (item.createdAt < (existing.since ?? item.createdAt)) existing.since = item.createdAt;
    // 只要有一条未读，这堆就算未读
    if (!item.readAt) existing.readAt = null;
  }
  return [...byKey.values()];
}

export interface NotificationGroup {
  label: string;
  items: StackedNotification[];
  unread: number;
}

/** 按项目分组；有未读的项目排前面，组内保持传入顺序 */
export function groupByProject(list: StackedNotification[]): NotificationGroup[] {
  const groups = new Map<string, NotificationGroup>();
  for (const item of list) {
    const key = item.projectId === null || item.projectId === undefined
      ? 'none'
      : String(item.projectId);
    const group = groups.get(key) ?? {
      label: item.projectNo || (key === 'none' ? '未关联项目' : `项目 ${key}`),
      items: [],
      unread: 0,
    };
    group.items.push(item);
    if (!item.readAt) group.unread += 1;
    groups.set(key, group);
  }
  return [...groups.values()].sort(
    (left, right) => right.unread - left.unread || right.items.length - left.items.length,
  );
}
